import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import InputField from "../components/input/InputField";
import BlueButton from "../components/buttons/BlueButton";
import { useSignUp } from "@clerk/clerk-react";
import Loading from "../components/common/Loading";

const SignUp = () => {
  const navigate = useNavigate();
  const { signUp, isLoaded } = useSignUp();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [firstName, setFirstName] = useState("");
  const [lastName, setLastName] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [isResending, setIsResending] = useState(false);
  const [isGoogleLoading, setIsGoogleLoading] = useState(false);
  const [isMicrosoftLoading, setIsMicrosoftLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [pendingVerification, setPendingVerification] = useState(false);
  const [code, setCode] = useState("");
  const [resendTimer, setResendTimer] = useState(0);

  useEffect(() => {
    let timer: NodeJS.Timeout;
    if (resendTimer > 0) {
      timer = setInterval(() => {
        setResendTimer((prev) => prev - 1);
      }, 1000);
    }
    return () => {
      if (timer) clearInterval(timer);
    };
  }, [resendTimer]);

  const formatTime = (seconds: number) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins}:${secs.toString().padStart(2, "0")}`;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    const isValidEmail = (email: string): boolean => {
      const emailRegex = /^[a-zA-Z0-9](?!.*?[.]{2})[a-zA-Z0-9._%+-]{0,63}[a-zA-Z0-9]@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/;
      return emailRegex.test(email.trim());
    };
    

    if (!email || !password || !firstName || !lastName || !isLoaded || !signUp)
      return;

    if (!isValidEmail(email)) {
      setError("Please enter a valid email address");
      return;
    }
    
    if(password.length < 8) {
      setError("Password must be at least 8 characters long");
      return;
    }

    setIsLoading(true);
    setError(null);

    try {
      await signUp.create({
        emailAddress: email,
        password,
        firstName,
        lastName,
      });

      await signUp.prepareEmailAddressVerification({ strategy: "email_code" });
      setPendingVerification(true);
      setResendTimer(120);
    } catch (err: any) {
      console.error("Error signing up:", err);
      if (err.errors?.[0]?.code === "form_identifier_exists") {
        setError(
          "An account with this email already exists. Please sign in instead."
        );
      } else if (err.errors?.[0]?.code === "form_password_pwned") {
        setError(
          "This password has been compromised in a data breach. Please choose a different password."
        );
      } else if (err.errors?.[0]?.code === "network_failure") {
        setError("Network error. Please check your connection and try again.");
      } else if (err.errors?.[0]?.code === "form_identifier_invalid") {
        setError("Please enter a valid email address.");
      } else if (err.errors?.[0]?.code === "form_password_validation_failed") {
        setError(
          "Password must be at least 8 characters long and include a mix of letters, numbers, and symbols."
        );
      } else if (err.errors?.[0]?.code === "form_param_format_invalid") {
        setError("Please ensure all fields are filled out correctly.");
      } else if (err.errors?.[0]?.code === "form_code_incorrect") {
        setError("Please enter a valid verification code.");
      } else {
        setError(
          err.errors?.[0]?.message || "Failed to sign up. Please try again."
        );
      }
    } finally {
      setIsLoading(false);
    }
  };

  const handleResendCode = async () => {
    if (!isLoaded || !signUp || resendTimer > 0) return;

    setIsResending(true);
    setError(null);

    try {
      await signUp.prepareEmailAddressVerification({ strategy: "email_code" });
      setResendTimer(120);
    } catch (err: any) {
      console.error("Error resending code:", err);
      setError(
        err.errors?.[0]?.message ||
        "Failed to resend verification code. Please try again."
      );
    } finally {
      setIsResending(false);
    }
  };

  const handleVerify = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!isLoaded || !signUp) return;

    setIsLoading(true);
    setError(null);

    try {
      const completeSignUp = await signUp.attemptEmailAddressVerification({
        code,
      });

      if (completeSignUp.status === "complete") {
        window.location.href = "/";
      }
    } catch (err: any) {
      console.error("Error verifying email:", err);
      setError(
        err.errors?.[0]?.message || "Failed to verify email. Please try again."
      );
    } finally {
      setIsLoading(false);
    }
  };

  const handleGoogleSignUp = async () => {
    setError(null);
    setIsGoogleLoading(true);
    try {
      await signUp?.authenticateWithRedirect({
        strategy: "oauth_google",
        redirectUrl: "/sso-callback",
        redirectUrlComplete: "/",
      });
    } catch (err: any) {
      console.error("Error signing up with Google:", err);
      setError(
        err.errors?.[0]?.message ||
        "Failed to sign up with Google. Please try again."
      );
    } finally {
      setIsGoogleLoading(false);
    }
  };

  const handleMicrosoftSignUp = async () => {
    setError(null);
    setIsMicrosoftLoading(true);
    try {
      await signUp?.authenticateWithRedirect({
        strategy: "oauth_microsoft",
        redirectUrl: "/sso-callback",
        redirectUrlComplete: "/",
      });
    } catch (err: any) {
      console.error("Error signing up with Microsoft:", err);
      setError(
        err.errors?.[0]?.message ||
        "Failed to sign up with Microsoft. Please try again."
      );
    } finally {
      setIsMicrosoftLoading(false);
    }
  };

  return (
    <div className="max-w-screen-md mx-auto px-4 py-2">
      <div className="max-w-md mx-auto bg-white p-8 rounded-2xl shadow-lg">
        <div className="text-center">
          <h2 className="text-2xl font-bold text-gray-900 mb-2">
            {pendingVerification ? "Verify your email" : "Create your account"}
          </h2>
          <span className="flex-1 text-sm text-gray-600">
            {pendingVerification
              ? `Please enter the verification code sent to ${email}`
              : "Welcome to Comicfy! Create your account to start creating amazing comics."}
          </span>
        </div>

        {error && (
          <div className="mt-4 p-3 bg-red-50 border border-red-200 rounded-lg">
            <p className="text-sm text-red-600">{error}</p>
          </div>
        )}

        {!pendingVerification ? (
          <form onSubmit={handleSubmit} className="mt-8 space-y-6">
            <div className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <InputField
                    label="First Name"
                    value={firstName}
                    onChange={(e) => setFirstName(e.target.value)}
                    placeholder="Enter your first name"
                    type="text"
                    required
                    autoComplete="given-name"
                  />
                </div>
                <div>
                  <InputField
                    label="Last Name"
                    value={lastName}
                    onChange={(e) => setLastName(e.target.value)}
                    placeholder="Enter your last name"
                    type="text"
                    required
                    autoComplete="family-name"
                  />
                </div>
              </div>
              <InputField
                label="Email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="Enter your email"
                type="email"
                required
                autoComplete="email"
              />
              <InputField
                label="Password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                placeholder="Create a password"
                type="password"
                required
                autoComplete="new-password"
              />
            </div>

            <div>
              <BlueButton
                label="Create Account"
                width="100%"
                height="45px"
                loading={isLoading}
              />
            </div>

            <div className="relative">
              <div className="absolute inset-0 flex items-center">
                <div className="w-full border-t border-gray-300" />
              </div>
              <div className="relative flex justify-center text-sm">
                <span className="px-2 bg-white text-gray-500">
                  Or continue with
                </span>
              </div>
            </div>

            <div className="space-y-3">
              <button
                type="button"
                onClick={handleGoogleSignUp}
                disabled={isGoogleLoading}
                className="w-full flex items-center justify-center gap-3 px-4 py-2.5 border border-gray-300 rounded-lg text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 transition-colors duration-200 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {isGoogleLoading ? (
                  <Loading />
                ) : (
                  <svg className="w-5 h-5" viewBox="0 0 24 24">
                    <path
                      fill="#4285F4"
                      d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"
                    />
                    <path
                      fill="#34A853"
                      d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"
                    />
                    <path
                      fill="#FBBC05"
                      d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"
                    />
                    <path
                      fill="#EA4335"
                      d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"
                    />
                  </svg>
                )}
                <span className="text-sm font-medium">
                  {isGoogleLoading ? "Connecting..." : "Continue with Google"}
                </span>
              </button>

              <button
                type="button"
                onClick={handleMicrosoftSignUp}
                disabled={isMicrosoftLoading}
                className="w-full flex items-center justify-center gap-3 px-4 py-2.5 border border-gray-300 rounded-lg text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 transition-colors duration-200 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {isMicrosoftLoading ? (
                  <Loading />
                ) : (
                  <svg className="w-5 h-5" viewBox="0 0 23 23">
                    <path fill="#f3f3f3" d="M0 0h23v23H0z" />
                    <path fill="#f35325" d="M1 1h10v10H1z" />
                    <path fill="#81bc06" d="M12 1h10v10H12z" />
                    <path fill="#05a6f0" d="M1 12h10v10H1z" />
                    <path fill="#ffba08" d="M12 12h10v10H12z" />
                  </svg>
                )}
                <span className="text-sm font-medium">
                  {isMicrosoftLoading ? "Connecting..." : "Continue with Microsoft"}
                </span>
              </button>
            </div>

            <p className="mt-2 text-sm text-gray-600 text-center">
              Already have an account?{" "}
              <button
                type="button"
                onClick={() => navigate("/login")}
                className="font-medium text-blue-600 hover:text-blue-500"
              >
                Sign in
              </button>
            </p>
          </form>
        ) : (
          <form onSubmit={handleVerify} className="mt-8 space-y-6">
            <div className="space-y-4">
              <InputField
                label="Verification Code"
                value={code}
                onChange={(e) => setCode(e.target.value)}
                placeholder="Enter verification code"
                type="text"
                required
              />
            </div>

            <div>
              <BlueButton
                label="Verify Email"
                width="100%"
                height="45px"
                loading={isLoading}
              />
            </div>

            <p className="mt-2 text-sm text-gray-600 text-center">
              {resendTimer > 0 ? (
                <span>Resend code in {formatTime(resendTimer)}</span>
              ) : (
                <>
                  Didn't receive the code?{" "}
                  <button
                    type="button"
                    onClick={handleResendCode}
                    disabled={isResending}
                    className="font-medium text-blue-600 hover:text-blue-500 disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    {isResending ? "Resending..." : "Resend code"}
                  </button>
                </>
              )}
            </p>
          </form>
        )}
      </div>
    </div>
  );
};

export default SignUp;
